// 원격 세션 탭 화면 — 위쪽 탭 줄 + 활성 탭의 세션 화면. 세션 창(layout="window")과
// 메인 창 대체 경로(layout="overlay", 팝업 차단 시)가 함께 쓴다.
//
// 비활성 탭도 **마운트된 채** 둔다(영상·연결 유지 — 탭을 바꿨다고 세션이 끊기면 안 된다). 키보드는 활성 탭만 받는다
// (SessionView 의 `active` — 비활성이 되는 순간 눌린 키를 모두 뗀다).
//
// 탭 끌기:
//   - **자기 탭 줄 안**(위아래 `REORDER_BAND_PX` 여유)에서 끌면 순서 바꾸기 — 끄는 동안 다른 탭이 실시간으로 비켜 주고
//     (Chrome 식, 2026-09-13 사용자 결정), 놓으면 그 순서로 확정한다. 탭이 2개 이상일 때만.
//   - 세션 창에서는 탭 줄 밖(창 밖·다른 세션 창)에 놓으면 새 창으로 분리 / 그 창으로 합치기(`onTabDrop`).
//   판정 순서: 다른 창 합치기(`onTabDrop` 이 넘겼다고 답함) → 자기 탭 줄이면 순서 바꾸기 → 그 밖은 무시.
//   합치기가 앞인 이유: 앱은 창이 겹친 곳에서 **위에 보이는 다른 창의 탭 줄**을 우선한다(Rust `pick_drop_target` ①) —
//   겹친 아래 창의 탭 줄 좌표와 같더라도 사용자가 보고 놓은 곳은 위 창이다.
//
// ⚠️ 끄는 동안의 순서는 CSS `order` 로 **보이는 순서만** 바꾼다(DOM 순서 = 실제 목록 순서 유지). React 가 DOM 노드를
//    옮기면 끌던 탭 요소가 문서에서 잠시 빠져 **포인터 캡처가 풀릴 수 있다**(놓기 이벤트 유실 = 탭 끌기가 멈춤).
import { useRef, useState } from 'react';
import type { SessionManager } from './useSessionManager';
import { isTabLive, type DropPoint, type SessionTab } from './types';
import { confirmAction } from '../ui/confirm';
import { useTabDrag } from './useTabDrag';
import { moveAt, nextDragIndex, reconcileOrder } from './tabOrder';
import { SessionView } from '../views/SessionView';

/** 끄는 동안 그림자에 붙는 안내. */
const DRAG_HINT_DETACH = '창 밖에 놓으면 새 창 · 다른 세션 창에 놓으면 합치기';
const DRAG_HINT_DOCK_ONLY = '다른 세션 창에 놓으면 합치기';
const DRAG_HINT_REORDER = '놓으면 이 자리로 이동';
/** 창 간 이동이 없는 화면(overlay)에서 탭 줄 밖으로 끌었을 때. */
const DRAG_HINT_REORDER_ONLY = '탭 줄 안에 놓으면 순서 바꾸기';

/**
 * 탭 줄 위아래로 이만큼(px) 벗어나도 "탭 줄 안"(순서 바꾸기)으로 본다 — 끌면서 손이 조금 흔들려도 순서 바꾸기가 풀리지 않게.
 * 이보다 멀어지면 미리보기 순서를 되돌리고 탭 줄 밖(분리·합치기) 판정으로 넘어간다.
 */
const REORDER_BAND_PX = 24;

/** 탭 단계 → 상태 점 색(styles.css `.dot`). */
function dotClass(t: SessionTab): string {
  if (t.phase === 'live') {
    return t.session?.ok === false ? 'err' : 'ok';
  }
  return t.phase === 'connecting' ? '' : 'err';
}

/** 영상 전(접속 중) 또는 끝난(실패·종료) 탭의 안내 화면. */
function TabStatus({ tab, mgr }: { tab: SessionTab; mgr: SessionManager }) {
  const busy = tab.phase === 'connecting';
  return (
    <div className="tab-status">
      <div className="tab-status-title">{tab.label}</div>
      <div className={busy ? 'hint' : 'tab-status-error'}>{tab.message ?? (busy ? '접속 중…' : '')}</div>
      <div className="row" style={{ gap: 8, marginTop: 12 }}>
        {!busy && <button onClick={() => mgr.retry(tab.id)}>다시 연결</button>}
        <button className="ghost" onClick={() => mgr.close(tab.id)}>
          {busy ? '취소' : '닫기'}
        </button>
      </div>
    </div>
  );
}

export function SessionTabs({
  mgr,
  layout,
  onHide,
  onTabDrop,
  onTabDragStart,
  onTabDragEnd,
}: {
  mgr: SessionManager;
  layout: 'window' | 'overlay';
  /** overlay 전용 — 세션을 유지한 채 메인 화면으로 돌아간다. */
  onHide?: () => void;
  /**
   * 세션 창 전용 — 탭을 끌어 놓았을 때 분리·합치기(판정은 호출측). 없으면 순서 바꾸기만 한다.
   * @returns 다른 곳(새 창·다른 세션 창)으로 넘기는 판정이었으면 true — 그때는 순서 바꾸기를 하지 않는다
   */
  onTabDrop?: (tab: SessionTab, pt: DropPoint) => Promise<boolean>;
  /** 세션 창 전용 — 끌기 시작·끝(창 밖 미리보기 켜고 끄기). */
  onTabDragStart?: (tab: SessionTab) => void;
  onTabDragEnd?: () => void;
}) {
  const stripRef = useRef<HTMLDivElement>(null);
  /** 탭 id → 탭 요소(끄는 동안 이웃 탭의 화면 위치를 잰다). */
  const tabEls = useRef(new Map<string, HTMLElement>());
  /**
   * 순서 미리보기(끄는 탭이 자기 탭 줄 안에 있을 때만, id 목록) — `null` 이면 실제 순서 그대로.
   * 포인터 처리기가 렌더 사이에도 최신 값을 보도록 참조 사본을 함께 둔다.
   */
  const [order, setOrder] = useState<string[] | null>(null);
  const orderRef = useRef<string[] | null>(null);
  const setPreview = (next: string[] | null) => {
    orderRef.current = next;
    setOrder(next);
  };

  const ids = mgr.tabs.map((t) => t.id);
  const shown = order ? reconcileOrder(order, ids) : ids;
  const findTab = (id: string) => mgr.tabs.find((t) => t.id === id);
  const canReorder = mgr.tabs.length > 1;

  /**
   * 포인터가 자기 탭 줄 안(위아래 여유 포함)인지. 가로는 탭 줄 폭 안이어야 한다.
   * 🔴 **창 내용 영역 밖은 제외** — 탭 줄이 창 맨 위라 위쪽 여유가 창 밖(브라우저 틀·제목 표시줄)까지 닿는데, 거기서 놓으면
   *    웹은 "창 밖 = 분리" 판정이 난다(실기 2026-09-13: 안내는 "이 자리로 이동" 인데 탭이 새 창으로 분리됐다). 안내와 결과가 같게.
   */
  const inStripBand = (x: number, y: number): boolean => {
    const r = stripRef.current?.getBoundingClientRect();
    const inViewport = x >= 0 && y >= 0 && x < window.innerWidth && y < window.innerHeight;
    return (
      !!r && inViewport && x >= r.left && x < r.right && y >= r.top - REORDER_BAND_PX && y < r.bottom + REORDER_BAND_PX
    );
  };

  /** 끄는 동안 — 탭 줄 안이면 이웃 탭 중심을 넘을 때마다 자리를 바꿔 보여 주고, 벗어나면 원래 순서로 되돌린다. */
  const onDragMove = (id: string, x: number, y: number) => {
    if (!canReorder || !inStripBand(x, y)) {
      if (orderRef.current) {
        setPreview(null);
      }
      return;
    }
    const cur = reconcileOrder(orderRef.current ?? ids, ids);
    const centers = cur.map((tid) => {
      const r = tabEls.current.get(tid)?.getBoundingClientRect();
      return r ? r.left + r.width / 2 : 0;
    });
    const from = cur.indexOf(id);
    const to = nextDragIndex(centers, from, x);
    if (!orderRef.current || to !== from) {
      setPreview(moveAt(cur, from, to)); // 처음 들어올 때도 설정(= "탭 줄 안" 표시 — 안내 문구가 바뀐다)
    }
  };

  /** 놓았다 — 다른 곳으로 넘기지 않았고 탭 줄 안이었으면 미리보기 순서로 확정한다. */
  const onDrop = (id: string, pt: DropPoint) => {
    const pending = orderRef.current;
    const commit = () => {
      if (pending) {
        mgr.move(id, pending.indexOf(id));
      }
      setPreview(null);
    };
    const tab = findTab(id);
    if (tab && onTabDrop) {
      // 판정이 끝날 때까지 미리보기 순서를 유지한다(확정과 같은 순서라 깜빡이지 않는다).
      void onTabDrop(tab, pt).then((handedOff) => (handedOff ? setPreview(null) : commit()));
    } else {
      commit();
    }
  };

  const { handlersFor, ghost, consumeClick } = useTabDrag({
    // 끌기는 창 간 이동이 있거나(세션 창) 순서를 바꿀 탭이 있을 때만 켠다(overlay 의 탭 1개 = 끌 이유 없음).
    onDrop: onTabDrop || canReorder ? onDrop : undefined,
    onDragStart: (id) => {
      const tab = findTab(id);
      if (tab) {
        onTabDragStart?.(tab);
      }
    },
    onDragMove,
    onDragEnd: (dropped) => {
      onTabDragEnd?.();
      if (!dropped) {
        setPreview(null); // 취소(캡처 상실 등) — 놓지 않았으니 순서도 그대로
      }
    },
  });
  const ghostTab = ghost ? mgr.tabs.find((t) => t.id === ghost.id) : undefined;
  const ghostHint = order
    ? DRAG_HINT_REORDER
    : !onTabDrop
      ? DRAG_HINT_REORDER_ONLY
      : canReorder
        ? DRAG_HINT_DETACH
        : DRAG_HINT_DOCK_ONLY;

  return (
    <div className={`session-tabs ${layout}`}>
      <div className="tab-strip" role="tablist" ref={stripRef}>
        {mgr.tabs.map((t) => (
          <div
            key={t.id}
            ref={(el) => {
              if (el) {
                tabEls.current.set(t.id, el);
              } else {
                tabEls.current.delete(t.id);
              }
            }}
            role="tab"
            aria-selected={t.id === mgr.activeId}
            className={`tab ${t.id === mgr.activeId ? 'active' : ''} ${ghost?.id === t.id ? 'dragging' : ''}`}
            style={{ order: shown.indexOf(t.id) }}
            title={t.message ?? t.label}
            {...handlersFor(t.id)}
            onClick={() => {
              if (!consumeClick()) {
                mgr.setActiveId(t.id);
              }
            }}
          >
            <span className={`dot ${dotClass(t)}`} />
            <span className="tab-label">{t.label}</span>
            <button
              className="tab-close"
              title="이 세션 닫기"
              onClick={async (e) => {
                e.stopPropagation();
                // 연결 중인 탭은 확인 받고 닫는다(사용자 요청 2026-09-27) — 끊긴·실패한 탭은 바로 닫는다(물을 것이 없다).
                if (isTabLive(t)) {
                  const ok = await confirmAction({
                    title: `${t.label} — 세션을 닫을까요?`,
                    message: '원격 PC와의 연결이 끊어집니다.',
                    confirmLabel: '세션 닫기',
                    danger: true,
                  });
                  if (!ok) return;
                }
                mgr.close(t.id);
              }}
            >
              ×
            </button>
          </div>
        ))}
        {/* 탭 뒤에 오도록 — 탭에 `order` 를 주므로 이 둘도 명시해야 한다(기본 0 이면 탭 앞으로 간다). */}
        <span className="grow" style={{ order: shown.length }} />
        {onHide && (
          <button className="ghost" onClick={onHide} style={{ marginRight: 8, order: shown.length + 1 }}>
            메인 화면
          </button>
        )}
      </div>
      <div className="tab-panels">
        {mgr.tabs.map((t) => (
          <div key={t.id} className="tab-panel" hidden={t.id !== mgr.activeId}>
            {t.phase === 'live' && t.session ? (
              <SessionView
                session={t.session}
                active={t.id === mgr.activeId}
                embedded
                label={t.label}
                onDisconnect={() => mgr.close(t.id)}
              />
            ) : (
              <TabStatus tab={t} mgr={mgr} />
            )}
          </div>
        ))}
      </div>
      {ghost && ghostTab && (
        <div className="tab-ghost" style={{ left: ghost.x, top: ghost.y }}>
          <span className={`dot ${dotClass(ghostTab)}`} />
          <span className="tab-label">{ghostTab.label}</span>
          <span className="tab-ghost-hint">{ghostHint}</span>
        </div>
      )}
    </div>
  );
}
